// src/AdaptiveSystem.js

export class AdaptiveSystem {
  constructor() {
    this.state = {
      recentOutcomes: [],
      weakTags: new Map(),
      weakSubthemes: new Map(), // New: Granular tracking
      sessionsCompleted: 0,
      lifetimeXp: 0,
      failedCases: []
    };
  }

  registerOutcome(node, isCorrect, isSlow) {
    this.state.recentOutcomes.push(isCorrect ? 1 : 0);
    if (this.state.recentOutcomes.length > 20) this.state.recentOutcomes.shift();

    if ((!isCorrect || isSlow)) {
      const weight = !isCorrect ? 1 : 0.5;
      
      // Track general tags
      if (node?.tags) {
        node.tags.forEach((tag) => {
          this.state.weakTags.set(tag, (this.state.weakTags.get(tag) || 0) + weight);
        });
      }

      // Track subtheme (The "Surgeon" part of the AI) [cite: 2, 8]
      if (node?.subtema) {
        const current = this.state.weakSubthemes.get(node.subtema) || 0;
        this.state.weakSubthemes.set(node.subtema, current + (weight * 2)); // Double weight for subthemes
      }
    }

    if (!isCorrect && node?.id) {
      if (!this.state.failedCases.includes(node.id)) {
        this.state.failedCases.push(node.id);
      }
    }
    if (isCorrect && node?.id) {
      const idx = this.state.failedCases.indexOf(node.id);
      if (idx > -1) this.state.failedCases.splice(idx, 1);
    }
  }

  getAccuracy() {
    if (this.state.recentOutcomes.length === 0) return 0.65;
    return this.state.recentOutcomes.reduce((a, b) => a + b, 0) / this.state.recentOutcomes.length;
  }

  computeShiftTags() {
    const accuracy = this.getAccuracy();
    const weakOrdered = [...this.state.weakTags.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([tag]) => tag)
      .filter((tag) => tag !== 'urgencias');

    if (accuracy < 0.55 && weakOrdered.length > 0) {
      return ['urgencias', weakOrdered[0]];
    }
    if (accuracy > 0.82) {
      return ['urgencias', 'algoritmo', 'tiempo_dependiente'];
    }
    return ['urgencias', ...(weakOrdered.slice(0, 1))];
  }

  buildUserProfile(economy, preferredDifficulty, allowRecent = false) {
    const ecoState = economy.exportState();
    const excludeIds = allowRecent ? [] : this.getRecentCaseIds(ecoState.caseHistory || {}, 6);
    
    return {
      playerLevel: ecoState.playerLevel || 1,
      weakTags: Array.from(this.state.weakTags.keys()),
      history: ecoState.caseHistory || {},
      excludeIds,
      difficulty: preferredDifficulty,
      activeMentor: ecoState.activeMentor,
      secondaryMentor: ecoState.secondaryMentor
    };
  }

  getRecentCaseIds(history, maxHours) {
    const now = Date.now();
    return Object.entries(history)
      .filter(([, ts]) => ts && now - ts < maxHours * 3600000)
      .map(([id]) => id);
  }

  getShiftModifier() {
    const accuracy = this.getAccuracy();
    if (accuracy < 0.55) {
      return { name: "Ola de refuerzo", type: "Cognitivo", effect: "Más casos en áreas débiles para fortalecer bases." };
    }
    if (accuracy > 0.82) {
      return { name: "Guardia Intensiva", type: "Ambiental", effect: "Aumento de frecuencia de protocolos y tiempo crítico." };
    }
    return { name: "Guardia Estándar", type: "Táctico", effect: "Balance general en presentación clínica y riesgo." };
  }

  exportState() {
    return {
      recentOutcomes: this.state.recentOutcomes,
      weakTags: Array.from(this.state.weakTags.entries()),
      weakSubthemes: Array.from(this.state.weakSubthemes.entries()),
      sessionsCompleted: this.state.sessionsCompleted,
      failedCases: this.state.failedCases
    };
  }

  importState(data) {
    if (!data) return;
    this.state.recentOutcomes = data.recentOutcomes || [];
    this.state.weakTags = new Map(data.weakTags || []);
    this.state.weakSubthemes = new Map(data.weakSubthemes || []);
    this.state.sessionsCompleted = data.sessionsCompleted || 0;
    this.state.failedCases = data.failedCases || [];
  }
}
